// dashboard/src/financials.js

// ── Budget type constants ─────────────────────────────────────────────────────
// Verified against Pulsar's Float instance (2026-04-17):
//   0 = None/Non-Billable (non_billable: 1 on project)
//   2 = Fee by Project    (has budget_total, confirmed in active projects)
//   3 = Time & Material   (budget_total: null, non_billable: 0)
// Types 1, 4 not currently used by Pulsar but kept for future use.
export const BUDGET_TYPES = {
  NONE:           0,
  HOURS_BUDGET:   1,  // not currently in use
  FEE_BY_PROJECT: 2,
  TIME_MATERIAL:  3,
  FEE_BY_PHASE:   4,  // not currently in use
  FIXED_FEE:      5,  // not currently in use
};

export const BUDGET_LABELS = {
  [BUDGET_TYPES.NONE]:           'None',
  [BUDGET_TYPES.HOURS_BUDGET]:   'Hours Budget',
  [BUDGET_TYPES.FEE_BY_PROJECT]: 'Fee by Project',
  [BUDGET_TYPES.TIME_MATERIAL]:  'Time & Material',
  [BUDGET_TYPES.FEE_BY_PHASE]:   'Fee by Phase',
  [BUDGET_TYPES.FIXED_FEE]:      'Fixed Fee',
};

const isFixedFeeType   = t => t === BUDGET_TYPES.FEE_BY_PROJECT || t === BUDGET_TYPES.FIXED_FEE;
const isFeeByPhaseType = t => t === BUDGET_TYPES.FEE_BY_PHASE;

// Bill rate lookup — mirrors rateFor() in App.jsx.
// Works with both task objects and logged-time entries (same people_id/phase_id shape).
function billRateFor(entry, proj, ptMap, pm, phasem = {}) {
  const pt          = (ptMap[proj.project_id] || {});
  // Float returns phase_id: 0 (integer) when there is no phase — treat 0 as "no phase"
  const phaseEntry  = (entry.phase_id && entry.phase_id > 0) ? (pt['ph'+entry.phase_id] || {}) : null;
  const ptPhaseRate = phaseEntry != null ? phaseEntry[entry.people_id] : undefined;
  const ptProjRate  = pt[entry.people_id];
  const person      = pm[entry.people_id] || {};
  const personBill  = parseFloat(person.default_hourly_rate || 0);
  const projRate    = parseFloat(proj.default_hourly_rate || 0);
  const phaseRate   = phaseEntry != null ? parseFloat((phasem[entry.phase_id] || {}).default_hourly_rate || 0) : 0;
  if (ptPhaseRate != null && ptPhaseRate > 0) return ptPhaseRate;
  if (ptProjRate  != null && ptProjRate  > 0) return ptProjRate;
  return phaseRate || personBill || projRate;
}

/**
 * Budget-type-aware revenue, cost and margin for one project in a time window.
 *
 * Important Float API notes:
 * - Logged-time entries do NOT include cost_rate; cost is pm[people_id].cost_rate × hours.
 * - entry.billable is integer 0|1, not boolean.
 * - entry.phase_id is 0 (not null) when no phase is assigned.
 * - proj.non_billable is 1 (integer) for non-billable projects.
 *
 * @param {object} proj       - Float project (budget_type, budget_total, non_billable, project_id)
 * @param {object} phasem     - phase_id → phase object (budget_total)
 * @param {Array}  allLogged  - ALL logged-time entries for this project (full history, unfiltered)
 * @param {Array}  allTasks   - ALL scheduled tasks for this project (full load window, unfiltered)
 * @param {string} winStart   - period start 'YYYY-MM-DD'
 * @param {string} winEnd     - period end   'YYYY-MM-DD'
 * @param {object} ptMap      - ptMap[projectId][peopleId] = billRate (App.jsx ptMap)
 * @param {object} pm         - people_id → person object (must have cost_rate)
 * @param {object} utils      - { wdCount, effEnd, fmt, toDate } passed from App.jsx
 *
 * @returns {{ budgetLabel, projectFee, scheduledRevenue, loggedRevenue, loggedCost, margin, marginPct }}
 */
export function revenueFor({ proj, phasem, allLogged, allTasks, winStart, winEnd, ptMap, pm, utils }) {
  const { wdCount, effEnd, fmt, toDate } = utils;
  const budgetType = proj.budget_type ?? BUDGET_TYPES.NONE;

  // Non-billable: proj.non_billable flag OR every task is non-billable
  const billableTasks = allTasks.filter(t => t.billable);
  const nonBillable   = !!proj.non_billable || (allTasks.length > 0 && billableTasks.length === 0);
  const label         = nonBillable ? 'Non-Billable' : (BUDGET_LABELS[budgetType] || 'None');

  // Cost: always from logged time in period.
  // Float logged-time entries don't carry cost_rate — look it up from the person.
  const loggedInPeriod = allLogged.filter(e => e.date >= winStart && e.date <= winEnd);
  const loggedCost     = Math.round(
    loggedInPeriod.reduce((s, e) => {
      const person = pm[e.people_id] || {};
      return s + parseFloat(e.hours || 0) * parseFloat(person.cost_rate || 0);
    }, 0)
  );

  if (nonBillable) {
    return { budgetLabel: label, projectFee: null, scheduledRevenue: null,
             loggedRevenue: null, loggedCost, margin: -loggedCost, marginPct: null };
  }

  // ── Scheduled hours helpers ───────────────────────────────────────────────
  // Total hours for a task list (full range, not period-filtered) — used as proration denominator
  const totalHrs = tasks => tasks.reduce((s, t) => {
    const person = pm[t.people_id] || {};
    const wdh    = person.work_days_hours || [0, 8, 8, 8, 8, 8, 0];
    const effE   = fmt(effEnd(t) || toDate(t.end_date));
    if (!effE || !t.start_date) return s;
    return s + parseFloat(t.hours || 0) * wdCount(t.start_date, effE, wdh);
  }, 0);

  // Hours for a task list clamped to the selected period — used as proration numerator
  const periodHrs = tasks => tasks.reduce((s, t) => {
    const person = pm[t.people_id] || {};
    const wdh    = person.work_days_hours || [0, 8, 8, 8, 8, 8, 0];
    const effE   = fmt(effEnd(t) || toDate(t.end_date));
    if (!effE || !t.start_date) return s;
    const ts = t.start_date < winStart ? winStart : t.start_date;
    const te = effE          > winEnd   ? winEnd   : effE;
    if (ts > te) return s;
    return s + parseFloat(t.hours || 0) * wdCount(ts, te, wdh);
  }, 0);

  // ── Fixed fee: Fee by Project or Fixed Fee ────────────────────────────────
  if (isFixedFeeType(budgetType)) {
    const projectFee        = parseFloat(proj.budget_total || 0);
    const totalLoggedHrs    = allLogged.reduce((s, e) => s + parseFloat(e.hours || 0), 0);
    const loggedHrsInPeriod = loggedInPeriod.reduce((s, e) => s + parseFloat(e.hours || 0), 0);
    const totalSchedHrs     = totalHrs(billableTasks);
    const schedHrsInPeriod  = periodHrs(billableTasks);
    const loggedRevenue     = totalLoggedHrs > 0 ? projectFee * (loggedHrsInPeriod / totalLoggedHrs) : 0;
    const scheduledRevenue  = totalSchedHrs  > 0 ? projectFee * (schedHrsInPeriod  / totalSchedHrs)  : 0;
    const margin            = Math.round(loggedRevenue - loggedCost);
    const marginPct         = loggedRevenue > 0 ? Math.round((margin / loggedRevenue) * 100) : null;
    return {
      budgetLabel: label, projectFee,
      scheduledRevenue: Math.round(scheduledRevenue),
      loggedRevenue:    Math.round(loggedRevenue),
      loggedCost, margin, marginPct,
    };
  }

  // ── Fee by Phase ──────────────────────────────────────────────────────────
  if (isFeeByPhaseType(budgetType)) {
    // Every phase on the project — tasks only cover the load window, so deriving phases from them drops budgeted phases
    const phaseIds = Object.values(phasem)
      .filter(ph => ph.project_id === proj.project_id && !ph.non_billable)
      .map(ph => ph.phase_id);
    let projectFee = 0, totalScheduledRevenue = 0, totalLoggedRevenue = 0;

    phaseIds.forEach(phaseId => {
      const phase    = phasem[phaseId];
      if (!phase || !parseFloat(phase.budget_total || 0)) return;
      const phaseFee = parseFloat(phase.budget_total);
      projectFee    += phaseFee;

      const phaseLogged            = allLogged.filter(e => e.phase_id === phaseId);
      const phaseLoggedInPeriod    = phaseLogged.filter(e => e.date >= winStart && e.date <= winEnd);
      const totalPhaseLoggedHrs    = phaseLogged.reduce((s, e) => s + parseFloat(e.hours || 0), 0);
      const phaseLoggedHrsInPeriod = phaseLoggedInPeriod.reduce((s, e) => s + parseFloat(e.hours || 0), 0);

      const phaseTasks            = billableTasks.filter(t => t.phase_id === phaseId);
      const totalPhaseSchedHrs    = totalHrs(phaseTasks);
      const phaseSchedHrsInPeriod = periodHrs(phaseTasks);

      totalLoggedRevenue    += totalPhaseLoggedHrs > 0 ? phaseFee * (phaseLoggedHrsInPeriod / totalPhaseLoggedHrs) : 0;
      totalScheduledRevenue += totalPhaseSchedHrs  > 0 ? phaseFee * (phaseSchedHrsInPeriod  / totalPhaseSchedHrs)  : 0;
    });

    const margin    = Math.round(totalLoggedRevenue - loggedCost);
    const marginPct = totalLoggedRevenue > 0 ? Math.round((margin / totalLoggedRevenue) * 100) : null;
    return {
      budgetLabel: label, projectFee: Math.round(projectFee),
      scheduledRevenue: Math.round(totalScheduledRevenue),
      loggedRevenue:    Math.round(totalLoggedRevenue),
      loggedCost, margin, marginPct,
    };
  }

  // ── Time & Material / None: hours × bill rate ─────────────────────────────
  let scheduledRevenue = 0, loggedRevenue = 0;

  // Scheduled revenue: billable tasks overlapping the period
  billableTasks
    .filter(t => {
      const effE = fmt(effEnd(t) || toDate(t.end_date));
      return effE && effE >= winStart && t.start_date <= winEnd;
    })
    .forEach(t => {
      const person = pm[t.people_id] || {};
      const wdh    = person.work_days_hours || [0, 8, 8, 8, 8, 8, 0];
      const ts     = t.start_date < winStart ? winStart : t.start_date;
      const effE   = fmt(effEnd(t) || toDate(t.end_date));
      const te     = effE > winEnd ? winEnd : effE;
      if (ts > te) return;
      const hrs  = parseFloat(t.hours || 0) * wdCount(ts, te, wdh);
      const bill = billRateFor(t, proj, ptMap, pm, phasem);
      if (bill) scheduledRevenue += hrs * bill;
    });

  // Logged revenue: only billable logged entries in period (billable is integer 0|1 in Float)
  loggedInPeriod.filter(e => !!e.billable).forEach(e => {
    const bill = billRateFor(e, proj, ptMap, pm, phasem);
    if (bill) loggedRevenue += parseFloat(e.hours || 0) * bill;
  });

  const margin    = Math.round(loggedRevenue - loggedCost);
  const marginPct = loggedRevenue > 0 ? Math.round((margin / loggedRevenue) * 100) : null;
  return {
    budgetLabel: label, projectFee: null,
    scheduledRevenue: Math.round(scheduledRevenue),
    loggedRevenue:    Math.round(loggedRevenue),
    loggedCost, margin, marginPct,
  };
}
